(() => {
  function money(cents, currency) {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  function initReveal(root) {
    const reveal = root.querySelector("[data-salsamba-reveal]");
    if (!reveal || reveal.dataset.salsambaRevealReady === "true") return;

    reveal.dataset.salsambaRevealReady = "true";
    const palms = Array.from(reveal.querySelectorAll("[data-salsamba-palm-source]"));
    const loadedSources = new Set();
    let fallbackTimer = 0;
    let destroyed = false;

    function open() {
      if (destroyed || !reveal.isConnected || reveal.classList.contains("is-ready")) return;
      window.clearTimeout(fallbackTimer);
      reveal.classList.add("is-ready");
    }

    function markReady(image) {
      const source = image.dataset.salsambaPalmSource;
      if (!source || loadedSources.has(source)) return;

      loadedSources.add(source);
      if (loadedSources.size >= 2) open();
    }

    function settleImage(image) {
      if (typeof image.decode === "function") {
        image.decode().catch(() => undefined).finally(() => markReady(image));
      } else {
        markReady(image);
      }
    }

    palms.forEach((image) => {
      if (image.complete) {
        settleImage(image);
        return;
      }

      image.addEventListener("load", () => settleImage(image), { once: true });
      image.addEventListener("error", () => markReady(image), { once: true });
    });

    function cleanup() {
      destroyed = true;
      window.clearTimeout(fallbackTimer);
    }

    fallbackTimer = window.setTimeout(open, 1200);
    window.addEventListener("pagehide", cleanup, { once: true });
  }

  function initForm(root) {
    if (root.dataset.salsambaReady === "true") return;

    const form = root.querySelector("[data-salsamba-form]");
    if (!form) return;

    const quantityInput = form.querySelector("[data-salsamba-quantity]");
    const quantityLabel = form.querySelector("[data-salsamba-quantity-label]");
    const variantInput = form.querySelector("[data-salsamba-variant]");
    const socialInput = form.querySelector("[data-salsamba-social-input]");
    const lessonInput = form.querySelector("[data-salsamba-lesson-input]");
    const socialRow = form.querySelector("[data-salsamba-social-row]");
    const lessonRow = form.querySelector("[data-salsamba-lesson-row]");
    const decrease = form.querySelector("[data-salsamba-decrease]");
    const increase = form.querySelector("[data-salsamba-increase]");
    const total = form.querySelector("[data-salsamba-total]");
    const status = form.querySelector("[data-salsamba-status]");
    const submit = form.querySelector("[data-salsamba-submit]");
    const submitLabel = form.querySelector("[data-salsamba-submit-label]");
    const donationFieldset = form.querySelector("[data-salsamba-donation]");
    const donationChoices = Array.from(form.querySelectorAll("[data-salsamba-donation-choice]"));
    const donationCustomWrap = form.querySelector("[data-salsamba-donation-custom-wrap]");
    const donationCustomInput = form.querySelector("[data-salsamba-donation-custom]");
    const donationError = form.querySelector("[data-salsamba-donation-error]");
    const cartError = form.querySelector("[data-salsamba-cart-error]");

    if (
      !quantityInput
      || !variantInput
      || !socialInput
      || !socialRow
      || !decrease
      || !increase
      || !total
      || !status
      || !submit
      || !submitLabel
    ) return;

    root.dataset.salsambaReady = "true";

    const social = {
      id: root.dataset.socialVariant,
      price: Number(root.dataset.socialPrice || 0),
      available: root.dataset.socialAvailable === "true",
    };
    const lesson = {
      id: root.dataset.lessonVariant,
      price: Number(root.dataset.lessonPrice || 0),
      available: root.dataset.lessonAvailable === "true",
    };
    const donations = {
      five: {
        id: root.dataset.donationFiveVariant,
        price: Number(root.dataset.donationFivePrice || 0),
      },
      ten: {
        id: root.dataset.donationTenVariant,
        price: Number(root.dataset.donationTenPrice || 0),
      },
      thirty: {
        id: root.dataset.donationThirtyVariant,
        price: Number(root.dataset.donationThirtyPrice || 0),
      },
      custom: {
        id: root.dataset.donationCustomVariant,
        price: Number(root.dataset.donationCustomPrice || 0),
      },
    };
    const currency = root.dataset.currency || "USD";
    const donationEnabled = root.dataset.donationEnabled === "true"
      && Boolean(donationFieldset)
      && donationChoices.length === 5
      && Boolean(donationCustomWrap)
      && Boolean(donationCustomInput)
      && Boolean(donationError)
      && Object.values(donations).every((donation) => donation.id && donation.price > 0);
    const donationVariantIds = new Set(
      Object.values(donations)
        .map((donation) => String(donation.id || ""))
        .filter(Boolean),
    );
    let isSubmitting = false;

    socialInput.disabled = !social.available;
    if (lessonInput) lessonInput.disabled = !lesson.available;
    decrease.disabled = false;
    increase.disabled = false;
    if (donationEnabled && donationFieldset) donationFieldset.disabled = false;

    function selectedTicket() {
      if (lessonInput && lessonInput.checked && lesson.available) return lesson;
      return social;
    }

    function selectedDonation() {
      if (!donationEnabled) {
        return { choice: "none", id: "", quantity: 0, amount: 0, valid: true, error: "" };
      }

      const checked = donationChoices.find((choice) => choice.checked);
      const choice = checked?.value || "none";
      if (choice === "none") {
        return { choice, id: "", quantity: 0, amount: 0, valid: true, error: "" };
      }

      if (choice !== "custom") {
        const selected = donations[choice];
        if (!selected?.id || selected.price <= 0) {
          return {
            choice,
            id: "",
            quantity: 0,
            amount: 0,
            valid: false,
            error: "That donation amount is currently unavailable.",
          };
        }

        return {
          choice,
          id: selected.id,
          quantity: 1,
          amount: selected.price,
          valid: true,
          error: "",
        };
      }

      const rawAmount = donationCustomInput?.value.trim() || "";
      const dollars = Number(rawAmount);
      const valid = /^\d+$/.test(rawAmount)
        && Number.isInteger(dollars)
        && dollars >= 1
        && dollars <= 1000;

      return {
        choice,
        id: donations.custom.id,
        quantity: valid ? dollars : 0,
        amount: valid ? donations.custom.price * dollars : 0,
        valid,
        error: valid ? "" : "Enter a whole-dollar donation from $1 to $1,000.",
      };
    }

    function render() {
      const count = Math.max(1, Math.min(8, Math.round(Number(quantityInput.value) || 1)));
      const selected = selectedTicket();
      const donation = selectedDonation();
      const lessonSelected = selected === lesson;
      const customDonationSelected = donation.choice === "custom";
      const noun = count === 1 ? "guest" : "guests";
      const ticketNoun = count === 1 ? "ticket" : "tickets";

      quantityInput.value = String(count);
      quantityInput.setAttribute("aria-label", `${count} ${noun} selected`);
      if (quantityLabel) quantityLabel.textContent = `${count} ${noun}`;

      decrease.disabled = count <= 1;
      increase.disabled = count >= 8;

      const nextVariant = String(selected.id || social.id || "");
      if (variantInput.value !== nextVariant) {
        variantInput.value = nextVariant;
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      socialRow.classList.toggle("is-selected", !lessonSelected);
      socialRow.classList.toggle("is-unavailable", !social.available);
      if (lessonRow) {
        lessonRow.classList.toggle("is-selected", lessonSelected);
        lessonRow.classList.toggle("is-unavailable", !lesson.available);
      }

      if (donationEnabled && donationCustomWrap && donationCustomInput && donationError) {
        donationCustomWrap.hidden = !customDonationSelected;
        donationCustomInput.disabled = !customDonationSelected;
        donationCustomInput.setAttribute("aria-invalid", String(customDonationSelected && !donation.valid));
        donationCustomInput.setCustomValidity(customDonationSelected ? donation.error : "");
        donationError.textContent = customDonationSelected ? donation.error : "";
        donationError.hidden = !customDonationSelected || donation.valid;
      }

      total.textContent = money((selected.price * count) + donation.amount, currency);
      submit.disabled = isSubmitting || !selected.available || !donation.valid;
      submitLabel.textContent = isSubmitting
        ? "Adding to cart…"
        : selected.available
          ? donation.amount > 0
            ? `Add ${count} ${ticketNoun} + donation to cart`
            : `Add ${count} ${ticketNoun} to cart`
          : lessonSelected
            ? "Lesson + Social sold out"
            : "Social tickets sold out";

      status.textContent = !selected.available
        ? "Tickets are currently unavailable."
        : lessonSelected
          ? `Group lesson and social selected for ${count} ${noun}.`
          : `Social admission selected for ${count} ${noun}.`;
    }

    decrease.addEventListener("click", () => {
      quantityInput.value = String(Number(quantityInput.value) - 1);
      render();
    });

    increase.addEventListener("click", () => {
      quantityInput.value = String(Number(quantityInput.value) + 1);
      render();
    });

    socialInput.addEventListener("change", render);
    if (lessonInput) lessonInput.addEventListener("change", render);

    donationChoices.forEach((choice) => {
      choice.addEventListener("change", () => {
        render();
        if (choice.value === "custom" && choice.checked && donationCustomInput) {
          donationCustomInput.focus();
        }
      });
    });
    if (donationCustomInput) donationCustomInput.addEventListener("input", render);

    async function removeExistingDonation() {
      const cartResponse = await fetch(root.dataset.cartJsonUrl || "/cart.js", {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      const cartPayload = await cartResponse.json().catch(() => ({}));

      if (!cartResponse.ok) {
        throw new Error(cartPayload.description || cartPayload.message || "We could not update your donation.");
      }

      const updates = {};
      (cartPayload.items || []).forEach((item) => {
        if (donationVariantIds.has(String(item.variant_id))) updates[item.key] = 0;
      });

      if (Object.keys(updates).length === 0) return;

      const updateResponse = await fetch(root.dataset.cartUpdateUrl || "/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates }),
      });
      const updatePayload = await updateResponse.json().catch(() => ({}));

      if (!updateResponse.ok) {
        throw new Error(updatePayload.description || updatePayload.message || "We could not update your donation.");
      }
    }

    if (donationEnabled) {
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (isSubmitting) return;

        const count = Math.max(1, Math.min(8, Math.round(Number(quantityInput.value) || 1)));
        const selected = selectedTicket();
        const donation = selectedDonation();

        if (!selected.available || !selected.id) {
          status.textContent = "Tickets are currently unavailable.";
          render();
          return;
        }

        if (!donation.valid) {
          render();
          donationCustomInput?.focus();
          return;
        }

        isSubmitting = true;
        if (cartError) {
          cartError.textContent = "";
          cartError.hidden = true;
        }
        render();

        const items = [{ id: Number(selected.id), quantity: count }];
        if (donation.id && donation.quantity > 0) {
          const donationItem = {
            id: Number(donation.id),
            quantity: donation.quantity,
            properties: {
              _salsamba_fundraiser: "Give2Cuba",
              Fundraiser: "Give2Cuba",
              "Donation amount": money(donation.amount, currency),
            },
          };

          items.push(donationItem);
        }

        try {
          await removeExistingDonation();

          const response = await fetch(root.dataset.cartAddUrl || "/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({ items }),
          });
          const payload = await response.json().catch(() => ({}));

          if (!response.ok) {
            throw new Error(payload.description || payload.message || "We could not add those items to the cart.");
          }

          window.location.assign(root.dataset.cartUrl || "/cart");
        } catch (error) {
          isSubmitting = false;
          if (cartError) {
            cartError.textContent = error instanceof Error
              ? error.message
              : "We could not add those items to the cart. Please try again.";
            cartError.hidden = false;
          }
          render();
        }
      });
    }

    render();
  }

  function initAll(scope = document) {
    scope.querySelectorAll("[data-salsamba-event]").forEach((root) => {
      initReveal(root);
      initForm(root);
    });
  }

  document.addEventListener("DOMContentLoaded", () => initAll());
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
